/**
 * Sidebar — Clean Rewrite v2.280.0
 *
 * Fixed-position navigation with 3 modes: pinned (260px), collapsed (64px),
 * flyout (collapsed + hover panel). Reads nav structure from sidebar-config.jsx.
 *
 * @prop {string} sidebarMode - 'pinned' | 'collapsed' | 'hover'
 * @prop {function} setSidebarMode - State setter from App.jsx
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useAppMode } from '../context/AppModeContext';
import { SIDEBAR_GROUPS, PORTAL_SECTIONS, JV_PARTNER_SECTIONS, HOME_ITEM, DASHBOARD_ITEM, TASKS_ITEM, MEINE_AUFGABEN_ITEM, ZEITERFASSUNG_ITEM, KALENDER_ITEM, ADMIN_ITEM, FOCUS_MODES } from '../config/sidebar-config.jsx';
import { ChevronRight, Eye, Pin, Settings, BookOpen, Code, ClipboardList, Filter, Bug } from 'lucide-react';
import BetaBadge from './BetaBadge';

const API = import.meta.env.VITE_API_URL || '';

// ── Helpers ──

function isVisible(item, permissions, isDev) {
  if (item.devOnly && !isDev) return false;
  if (!item.modul) return false;
  // #1426: Komma-separierte Module = ODER-Logik (wie ROUTE_TO_MODUL in App.jsx)
  return item.modul.split(',').some(m => {
    const p = permissions?.[m];
    return p === 'full' || p === 'team' || p === 'read';
  });
}

function isReadOnly(item, permissions) {
  return item.modul ? permissions?.[item.modul] === 'read' : false;
}

function sectionBadge(items, badges) {
  let n = 0;
  for (const item of items) {
    if (item.hasBadge && badges[item.hasBadge]) n += badges[item.hasBadge];
    if (item.subItems) for (const sub of item.subItems) if (sub.hasBadge && badges[sub.hasBadge]) n += badges[sub.hasBadge];
  }
  return n;
}

// ── Sub-components ──

function SidebarItem({ item, t, badge, readOnly, active }) {
  const label = t(item.labelKey);
  return (
    <NavLink
      to={item.path}
      end
      className={({ isActive }) => {
        const a = isActive || active;
        return `flex items-center gap-3 py-[10px] mx-3 my-[1px] rounded-[10px] transition-all duration-150 border-l-[3px] pl-[13px] pr-4 ${
          a ? 'border-[var(--sidebar-active)] text-[var(--sidebar-active)] font-semibold' : 'border-transparent text-[var(--sidebar-text)] hover:text-[var(--sidebar-text-hover)] hover:bg-[var(--sidebar-bg-hover)]'
        }`;
      }}
    >
      {({ isActive }) => (<>
        <span className={`flex-shrink-0 ${(isActive || active) ? 'text-[var(--sidebar-active)]' : 'text-[var(--sidebar-category)]'}`}><item.Icon /></span>
        <span className="text-[14px] truncate flex-1">{label}</span>
        {item.beta && <BetaBadge size="sm" className="flex-shrink-0" />}
        {readOnly && <Eye style={{ width: 12, height: 12 }} className="flex-shrink-0 text-[var(--sidebar-category)] opacity-60" />}
        {badge > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold text-white leading-none bg-red-500">{badge}</span>}
      </>)}
    </NavLink>
  );
}

function CollapsedIcon({ item, badge, t, onHover, onLeave }) {
  const location = useLocation();
  const isActive = item.path
    ? location.pathname === item.path || location.pathname.startsWith(item.path + '/')
    : item.subItems?.some(s => location.pathname === s.path || location.pathname.startsWith(s.path + '/'));
  const label = t(item.labelKey);
  const El = item.path ? NavLink : 'button';
  const props = item.path ? { to: item.path, end: true } : {};
  return (
    <El
      {...props}
      onMouseEnter={(e) => onHover?.(e, item)}
      onMouseLeave={onLeave}
      className={`flex items-center justify-center w-10 h-10 mx-auto rounded-[10px] transition-colors relative ${
        isActive ? 'text-[var(--sidebar-active)]' : 'text-[var(--sidebar-category)] hover:text-[var(--sidebar-text-hover)] hover:bg-[var(--sidebar-bg-hover)]'
      }`}
      title={label}
    >
      <item.Icon />
      {badge > 0 && <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-red-500" />}
    </El>
  );
}

function SubItem({ item, t, badge }) {
  const label = t(item.labelKey);
  return (
    <NavLink
      to={item.path}
      end
      className={({ isActive }) =>
        `flex items-center gap-2 py-[7px] pl-[52px] pr-4 mx-3 my-[1px] border-l-[3px] rounded-[8px] transition-colors text-[13px] ${
          isActive ? 'border-[var(--sidebar-active)] text-[var(--sidebar-active)] font-medium' : 'border-transparent text-[var(--sidebar-text)] hover:bg-[var(--sidebar-bg-hover)]'
        }`
      }
    >
      <span className="flex-1 truncate">{label}</span>
      {badge > 0 && <span className="ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-bold text-white leading-none bg-red-500">{badge}</span>}
    </NavLink>
  );
}

function ItemWithSub({ item, t, openMenus, toggleMenu, badge, badges, permissions }) {
  const location = useLocation();
  const subs = (item.subItems || []).filter(s => !s.requireFull || permissions?.[item.modul] === 'full');
  const label = t(item.labelKey);
  const menuId = item.modul || label;
  const isOpen = openMenus[menuId];
  const hasActiveSub = subs.some(s => location.pathname === s.path || location.pathname.startsWith(s.path + '/'));
  const isOwnPathActive = item.path && (location.pathname === item.path || location.pathname.startsWith(item.path + '/'));
  const isParentActive = hasActiveSub || isOwnPathActive;
  const subBadgeTotal = subs.reduce((sum, s) => sum + (s.hasBadge && badges[s.hasBadge] ? badges[s.hasBadge] : 0), 0);
  const totalBadge = badge > 0 ? badge : subBadgeTotal;

  useEffect(() => {
    if (isParentActive && !isOpen) toggleMenu(menuId);
  }, [isParentActive]); // eslint-disable-line

  // #1058: Items with BOTH path and subItems → top label navigates, separate chevron toggles submenu
  if (item.path) {
    return (
      <div>
        <div
          className={`flex items-center gap-3 py-[10px] mx-3 my-[1px] rounded-[10px] transition-all duration-150 border-l-[3px] pl-[13px] pr-1 ${
            isParentActive && !isOpen ? 'border-[var(--sidebar-active)] text-[var(--sidebar-active)] font-semibold' : 'border-transparent text-[var(--sidebar-text)] hover:text-[var(--sidebar-text-hover)] hover:bg-[var(--sidebar-bg-hover)]'
          }`}
          style={{ width: 'calc(100% - 24px)' }}
        >
          <NavLink
            to={item.path}
            end
            className={({ isActive }) => `flex items-center gap-3 flex-1 min-w-0 ${isActive ? 'text-[var(--sidebar-active)] font-semibold' : ''}`}
          >
            <span className={`flex-shrink-0 ${isParentActive ? 'text-[var(--sidebar-active)]' : 'text-[var(--sidebar-category)]'}`}><item.Icon /></span>
            <span className="text-[14px] truncate flex-1 text-left">{label}</span>
            {!isOpen && totalBadge > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold text-white leading-none bg-red-500">{totalBadge}</span>}
          </NavLink>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleMenu(menuId); }}
            className="p-1 rounded hover:bg-[var(--sidebar-bg-hover)] flex-shrink-0"
            aria-label={isOpen ? 'Collapse menu' : 'Expand menu'}
          >
            <ChevronRight className={`w-4 h-4 opacity-40 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
          </button>
        </div>
        <div className="overflow-hidden transition-all duration-200" style={{ maxHeight: isOpen ? `${subs.length * 40}px` : '0px' }}>
          {subs.map(s => <SubItem key={s.path} item={s} t={t} badge={s.hasBadge && badges[s.hasBadge] ? badges[s.hasBadge] : 0} />)}
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => toggleMenu(menuId)}
        className={`flex items-center gap-3 py-[10px] mx-3 my-[1px] rounded-[10px] transition-all duration-150 border-l-[3px] pl-[13px] pr-4 ${
          isParentActive && !isOpen ? 'border-[var(--sidebar-active)] text-[var(--sidebar-active)] font-semibold' : 'border-transparent text-[var(--sidebar-text)] hover:text-[var(--sidebar-text-hover)] hover:bg-[var(--sidebar-bg-hover)]'
        }`}
        style={{ width: 'calc(100% - 24px)' }}
      >
        <span className={`flex-shrink-0 ${isParentActive ? 'text-[var(--sidebar-active)]' : 'text-[var(--sidebar-category)]'}`}><item.Icon /></span>
        <span className="text-[14px] truncate flex-1 text-left">{label}</span>
        {!isOpen && totalBadge > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold text-white leading-none bg-red-500">{totalBadge}</span>}
        <ChevronRight className={`w-4 h-4 opacity-40 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      <div className="overflow-hidden transition-all duration-200" style={{ maxHeight: isOpen ? `${subs.length * 40}px` : '0px' }}>
        {subs.map(s => <SubItem key={s.path} item={s} t={t} badge={s.hasBadge && badges[s.hasBadge] ? badges[s.hasBadge] : 0} />)}
      </div>
    </div>
  );
}

function Flyout({ item, items, t, badges, permissions, onMouseEnter, onMouseLeave }) {
  if (!item) return null;
  const label = t(item.labelKey);
  // For parent items with subItems, show sub-items; for regular items with no sub, just show the label
  const subs = item.subItems
    ? item.subItems.filter(s => !s.requireFull || permissions?.[item.modul] === 'full')
    : null;

  return (
    <div
      className="fixed rounded-xl shadow-sm py-2 min-w-[200px] z-[60]"
      style={{ left: 68, top: item._flyoutTop || 0, backgroundColor: 'var(--sidebar-bg)', border: '1px solid var(--sidebar-border)' }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--sidebar-category)]">{label}</div>
      {subs ? subs.map(s => (
        <NavLink
          key={s.path}
          to={s.path}
          end
          className={({ isActive }) =>
            `block px-3 py-2 text-sm transition-colors ${isActive ? 'text-[var(--sidebar-active)] font-medium' : 'text-[var(--sidebar-text)] hover:text-[var(--sidebar-text-hover)] hover:bg-[var(--sidebar-bg-hover)]'}`
          }
        >
          {t(s.labelKey)}
        </NavLink>
      )) : (
        <NavLink to={item.path} className="block px-3 py-2 text-sm text-[var(--sidebar-text)] hover:text-[var(--sidebar-text-hover)] hover:bg-[var(--sidebar-bg-hover)]">
          {label}
        </NavLink>
      )}
    </div>
  );
}

// ── Main Sidebar ──

export default function Sidebar({ sidebarMode, setSidebarMode }) {
  const isPinned = sidebarMode === 'pinned';
  const collapsed = sidebarMode === 'collapsed';
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { mode: appMode, ready: appModeReady } = useAppMode();
  const location = useLocation();
  const permissions = user?.permissions || {};

  const [isDev, setIsDev] = useState(false);
  const [sidebarIcon, setSidebarIcon] = useState(null);
  const [appVersion, setAppVersion] = useState('');
  const [badges, setBadges] = useState({});
  const [flyoutItem, setFlyoutItem] = useState(null);
  const flyoutTimeout = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const hoverRef = useRef(null);
  const [focusDropdown, setFocusDropdown] = useState(false);

  // Focus mode (UX filter, persisted in localStorage)
  const [focusMode, setFocusMode] = useState(() => {
    try { return localStorage.getItem('weship_sidebar_focus') || 'alle'; } catch { return 'alle'; }
  });
  const changeFocusMode = useCallback((mode) => {
    setFocusMode(mode);
    setFocusDropdown(false);
    try { localStorage.setItem('weship_sidebar_focus', mode); } catch {}
  }, []);

  // Open menus (multi-open, persisted)
  const [openMenus, setOpenMenus] = useState(() => {
    try { return JSON.parse(localStorage.getItem('weship-sb-menus') || '{}'); } catch { return {}; }
  });

  // Open groups (multi-open, persisted) — default collapsed for new users
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const stored = localStorage.getItem('weship-sb-groups');
      if (stored) return JSON.parse(stored);
      // New user: all groups collapsed (auto-expand handles active group)
      const defaults = {};
      SIDEBAR_GROUPS.forEach(s => { defaults[s.labelKey] = false; });
      return defaults;
    } catch { return {}; }
  });

  const toggleMenu = useCallback((id) => {
    setOpenMenus(prev => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem('weship-sb-menus', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const toggleGroup = useCallback((key) => {
    setOpenGroups(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('weship-sb-groups', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  // #1360 (C): Accordion-beim-Navigieren — aktive Gruppe öffnen, Geschwister-Gruppen
  // einklappen. Hält das Menü kompakt, damit die Geschwister-Punkte des aktiven Moduls
  // ohne langes Scrollen erreichbar bleiben. Manuelles Toggle bleibt erlaubt (re-kompaktiert
  // erst bei der nächsten Navigation). Ohne aktive Gruppe (z.B. Home/Dashboard) unverändert.
  useEffect(() => {
    let activeKey = null;
    for (const s of SIDEBAR_GROUPS) {
      for (const item of s.items) {
        const match = (item.path && (location.pathname === item.path || location.pathname.startsWith(item.path + '/')))
          || item.subItems?.some(sub => location.pathname === sub.path || location.pathname.startsWith(sub.path + '/'));
        if (match) { activeKey = s.labelKey; break; }
      }
      if (activeKey) break;
    }
    if (!activeKey) return;
    setOpenGroups(prev => {
      const alreadyAccordion = SIDEBAR_GROUPS.every(s => !!prev[s.labelKey] === (s.labelKey === activeKey));
      if (alreadyAccordion) return prev;
      const next = {};
      SIDEBAR_GROUPS.forEach(s => { next[s.labelKey] = s.labelKey === activeKey; });
      try { localStorage.setItem('weship-sb-groups', JSON.stringify(next)); } catch {}
      return next;
    });
  }, [location.pathname]); // eslint-disable-line

  // #1360 (C): aktiven Eintrag bei Routenwechsel sicher in den sichtbaren Bereich holen
  // (greift auch, falls eine einzelne Gruppe selbst lang ist). Timeout = nach maxHeight-Transition.
  const navRef = useRef(null);
  useEffect(() => {
    const id = setTimeout(() => {
      const el = navRef.current?.querySelector('a[aria-current="page"]');
      if (el) el.scrollIntoView({ block: 'nearest' });
    }, 230);
    return () => clearTimeout(id);
  }, [location.pathname, openGroups]);

  // Fetch isDev + version + sidebar icon
  useEffect(() => {
    fetch('/api/health').then(r => r.json()).then(d => { if (d.env === 'development') setIsDev(true); }).catch(() => {});
    fetch(`${API}/api/version`, { credentials: 'include' }).then(r => r.ok ? r.json() : null).then(d => { if (d?.version) setAppVersion(d.version); }).catch(() => {});
    fetch(`${API}/api/einstellungen/sidebar-icon`, { credentials: 'include' }).then(r => r.ok ? r.json() : null).then(d => { if (d?.url) setSidebarIcon(`${API}${d.url}`); }).catch(() => {});
  }, []);

  // Fetch badge counts
  useEffect(() => {
    let mounted = true;
    async function fetchBadges() {
      try {
        const [casesR, syncR, newsR, urlaubR, krankR, taskR, salesR, fcR, azR, recR, userTasksR, bugR] = await Promise.all([
          fetch(`${API}/api/cases/my-count`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
          fetch(`${API}/api/data-hub/sync-jobs`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
          fetch(`${API}/api/portal/news/ungelesen`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
          fetch(`${API}/api/portal/meine-abwesenheiten/badge`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
          fetch(`${API}/api/urlaub/krankenstaende?ohne_bestaetigung=true`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
          fetch(`${API}/api/tasks/count`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
          fetch(`${API}/api/sales/ungelesen-count`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
          fetch(`${API}/api/customer-forecasts/pending`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
          fetch(`${API}/api/aktionszeitraeume?status=eingereicht`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
          fetch(`${API}/api/recruiting/ungelesen-count`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
          fetch(`${API}/api/user-tasks/stats`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
          fetch(`${API}/api/bug-reports/badge-count`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
        ]);
        if (!mounted) return;
        setBadges({
          cases: casesR?.count || 0,
          datahub: (syncR?.jobs || []).filter(j => j.last?.status === 'error').length,
          portal_news: newsR?.count || 0,
          portal_urlaub: urlaubR?.count || 0,
          portal_krankmeldung: (krankR?.data || []).filter(k => !k.bestaetigung).length,
          tasks: taskR?.kritisch || 0,
          sales_unread: salesR?.count || 0,
          fc_pending: Array.isArray(fcR) ? fcR.length : 0,
          az_pending: Array.isArray(azR) ? azR.length : 0,
          recruiting_ungelesen: recR?.count || 0,
          user_tasks: (userTasksR?.inbox_neu || 0) + (userTasksR?.ueberfaellig || 0),
          bug_reports: bugR?.count || 0,
        });
      } catch { /* silent */ }
    }
    fetchBadges();
    const iv = setInterval(fetchBadges, 120000);
    // #1054 — Sales-Counter sofort neu laden, wenn Mail-State im UI geändert wird
    const onMailChanged = () => { fetchBadges(); };
    window.addEventListener('weship:mail-changed', onMailChanged);
    return () => {
      mounted = false;
      clearInterval(iv);
      window.removeEventListener('weship:mail-changed', onMailChanged);
    };
  }, []);

  // Flyout handlers
  const showFlyout = useCallback((e, item) => {
    clearTimeout(flyoutTimeout.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setFlyoutItem({ ...item, _flyoutTop: rect.top });
  }, []);
  const hideFlyout = useCallback(() => {
    flyoutTimeout.current = setTimeout(() => setFlyoutItem(null), 200);
  }, []);
  const keepFlyout = useCallback(() => { clearTimeout(flyoutTimeout.current); }, []);

  // Hover-expand (collapsed → temporary full view overlay)
  const handleSidebarEnter = useCallback(() => {
    if (!isPinned) { clearTimeout(hoverRef.current); setIsHovering(true); setFlyoutItem(null); }
  }, [isPinned]);
  const handleSidebarLeave = useCallback(() => {
    hoverRef.current = setTimeout(() => setIsHovering(false), 250);
  }, []);

  const showLabels = isPinned || isHovering;

  // ── Render helpers ──

  const renderItem = (item) => {
    const ro = isReadOnly(item, permissions);
    if (item.subItems) {
      return <ItemWithSub key={item.labelKey} item={item} t={t} openMenus={openMenus} toggleMenu={toggleMenu} badge={item.hasBadge ? badges[item.hasBadge] : 0} badges={badges} permissions={permissions} />;
    }
    return <SidebarItem key={item.labelKey || item.path} item={item} t={t} badge={item.hasBadge ? badges[item.hasBadge] : 0} readOnly={ro} />;
  };

  const renderGroup = (section, items) => {
    if (!items.length) return null;
    const key = section.labelKey;
    const isOpen = !showLabels || openGroups[key] === true; // default collapsed
    const badge = sectionBadge(items, badges);

    if (!showLabels) {
      return (
        <div key={key}>
          <div className="my-2 border-t border-[var(--sidebar-border)] mx-3" />
          {items.map(item => (
            <CollapsedIcon key={item.labelKey || item.path} item={item} t={t} badge={item.hasBadge ? badges[item.hasBadge] : 0} onHover={item.subItems ? showFlyout : undefined} onLeave={item.subItems ? hideFlyout : undefined} />
          ))}
        </div>
      );
    }

    // #1724: Gruppe ohne eigenen Header (Prozesse) — der Collapse-Parent trägt den Namen,
    // sonst würde das Label doppelt erscheinen (Gruppen-Header + Collapse-Parent).
    if (section.hideHeader) {
      return <div key={key} className="mt-5">{items.map(renderItem)}</div>;
    }

    const maxH = items.reduce((s, i) => s + 48 + (i.subItems?.length || 0) * 40, 0);
    return (
      <div key={key}>
        <button onClick={() => toggleGroup(key)} className="mt-5 mb-2 px-4 mx-3 flex items-center justify-between" style={{ width: 'calc(100% - 24px)' }}>
          <span className="text-[11px] font-semibold uppercase tracking-[0.8px] text-[var(--sidebar-category)]">{t(key)}</span>
          <div className="flex items-center gap-1.5">
            {!isOpen && badge > 0 && <span className="px-1.5 py-0.5 rounded-full text-[8px] font-bold text-white leading-none bg-red-500">{badge}</span>}
            <ChevronRight className={`w-3 h-3 text-[var(--sidebar-category)] transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
          </div>
        </button>
        <div className="overflow-hidden transition-all duration-200" style={{ maxHeight: isOpen ? `${maxH}px` : '0px' }}>
          {items.map(renderItem)}
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════

  const spacerW = isPinned ? 260 : 64;
  const visualW = showLabels ? 260 : 64;

  return (
    <aside style={{ width: spacerW, transition: 'width 200ms ease', flexShrink: 0, overflow: 'visible' }}>
      <div
        onMouseEnter={handleSidebarEnter}
        onMouseLeave={handleSidebarLeave}
        style={{
          position: 'fixed', top: 0, left: 0, width: visualW, height: '100vh',
          zIndex: isHovering && !isPinned ? 55 : 50, transition: 'width 200ms ease',
          display: 'flex', flexDirection: 'column',
          backgroundColor: 'var(--sidebar-bg)',
          // Border-Right via inset box-shadow — immer voll durchgehend, theme-unabhaengig,
          // kein box-model Impact, nicht durch Flex-Kinder unterbrechbar (#718)
          boxShadow: isHovering && !isPinned
            ? 'inset -1px 0 0 rgba(255,255,255,0.08), 4px 0 24px rgba(0,0,0,0.25)'
            : 'inset -1px 0 0 rgba(255,255,255,0.08)',
        }}
      >
        {/* ── Logo ── */}
        <div
          onClick={() => navigate('/')}
          className="h-12 min-h-[48px] flex items-center px-4 border-b border-[var(--sidebar-border)] flex-shrink-0 cursor-pointer gap-3"
        >
          {sidebarIcon ? (
            <img src={sidebarIcon} alt="WeShip" className="w-7 h-7 min-w-[28px] rounded-lg object-contain flex-shrink-0" />
          ) : (
            <div className="w-7 h-7 min-w-[28px] rounded-lg flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0" style={{ background: 'linear-gradient(135deg, var(--primary) 0%, var(--accent-blue) 100%)' }}>
              we
            </div>
          )}
          {showLabels && (
            <div className="flex flex-col min-w-0">
              <span className="text-[15px] font-semibold text-[var(--sidebar-logo-text)] leading-tight">WeShip</span>
              {appVersion && <span className="text-[10px] text-[var(--sidebar-version)] leading-tight">v{appVersion}</span>}
            </div>
          )}
        </div>

        {/* ── Focus Picker ── */}
        {showLabels ? (
          <div className="px-3 pt-3 pb-1 flex-shrink-0">
            <div className="rounded-[10px] p-[3px] flex gap-1" style={{ backgroundColor: 'var(--sidebar-focus-bg)', border: '1px solid var(--sidebar-border)' }}>
              {FOCUS_MODES.map(mode => (
                <button
                  key={mode.key}
                  onClick={() => changeFocusMode(mode.key)}
                  className={`flex-1 px-1 py-1.5 rounded-[8px] text-[11px] font-medium transition-all whitespace-nowrap ${
                    focusMode === mode.key
                      ? 'text-[var(--sidebar-focus-active-text)]'
                      : 'text-[var(--sidebar-focus-text)] hover:text-[var(--sidebar-text-hover)]'
                  }`}
                  style={focusMode === mode.key ? { backgroundColor: 'var(--sidebar-focus-active-bg)' } : {}}
                >
                  {t(mode.labelKey)}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="pt-3 pb-1 flex-shrink-0 relative">
            <button
              onClick={() => setFocusDropdown(v => !v)}
              className={`flex items-center justify-center w-10 h-10 mx-auto rounded-[10px] transition-colors ${
                focusMode !== 'alle' ? 'text-[var(--sidebar-active)]' : 'text-[var(--sidebar-category)] hover:text-[var(--sidebar-text-hover)] hover:bg-[var(--sidebar-bg-hover)]'
              }`}
              title={t(FOCUS_MODES.find(m => m.key === focusMode)?.labelKey || '')}
            >
              <Filter className="w-[18px] h-[18px]" />
            </button>
            {focusDropdown && (
              <div className="absolute left-[68px] top-2 rounded-xl shadow-sm py-1 min-w-[120px] z-[60]" style={{ backgroundColor: 'var(--sidebar-bg)', border: '1px solid var(--sidebar-border)' }}>
                {FOCUS_MODES.map(mode => (
                  <button
                    key={mode.key}
                    onClick={() => changeFocusMode(mode.key)}
                    className={`block w-full text-left px-3 py-2 text-sm transition-colors ${
                      focusMode === mode.key ? 'text-[var(--sidebar-active)] font-medium' : 'text-[var(--sidebar-text)] hover:text-[var(--sidebar-text-hover)] hover:bg-[var(--sidebar-bg-hover)]'
                    }`}
                  >
                    {t(mode.labelKey)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Navigation (scrollable) ── */}
        <nav ref={navRef} className="flex-1 overflow-y-auto overflow-x-hidden pt-3 sidebar-nav">
          {user?.rolle === 'jv_partner' ? (
            JV_PARTNER_SECTIONS.map(s => renderGroup(s, s.items.filter(i => isVisible(i, permissions, isDev))))
          ) : appModeReady && appMode === 'portal' ? (
            /* Portal mode — NUR Portal-Einträge, kein Home/Dashboard/Tasks */
            PORTAL_SECTIONS.map(s => renderGroup(s, s.items))
          ) : appModeReady && ['portal_mitarbeiter', 'portal_kunde'].includes(user?.rolle) ? (
            /* Pure portal users — only portal sections */
            PORTAL_SECTIONS.map(s => renderGroup(s, s.items))
          ) : (<>
            {/* Home + Dashboard + Tasks + Kalender — gated by 'basis' module */}
            {isVisible(HOME_ITEM, permissions, isDev) && (
              !showLabels
                ? <CollapsedIcon item={HOME_ITEM} t={t} badge={0} />
                : <SidebarItem item={HOME_ITEM} t={t} badge={0} />
            )}
            {isVisible(DASHBOARD_ITEM, permissions, isDev) && (
              !showLabels
                ? <CollapsedIcon item={DASHBOARD_ITEM} t={t} badge={0} />
                : <SidebarItem item={DASHBOARD_ITEM} t={t} badge={0} />
            )}
            {user?.dashboard_rollen?.length > 0 && isVisible(TASKS_ITEM, permissions, isDev) && (
              !showLabels
                ? <CollapsedIcon item={TASKS_ITEM} t={t} badge={badges.tasks || 0} />
                : <SidebarItem item={TASKS_ITEM} t={t} badge={badges.tasks || 0} />
            )}
            {isVisible(MEINE_AUFGABEN_ITEM, permissions, isDev) && (
              !showLabels
                ? <CollapsedIcon item={MEINE_AUFGABEN_ITEM} t={t} badge={badges.user_tasks || 0} />
                : <SidebarItem item={MEINE_AUFGABEN_ITEM} t={t} badge={badges.user_tasks || 0} />
            )}
            {/* #995 P2 — Zeiterfassung im persönlichen User-Menü; sichtbar bei zeiterfassung_aktiv ODER Admin-Rolle */}
            {(user?.zeiterfassung_aktiv === true || ['administrator','ops_manager','cs_teamlead','controlling'].includes(user?.rolle)) && isVisible(ZEITERFASSUNG_ITEM, permissions, isDev) && (
              !showLabels
                ? <CollapsedIcon item={ZEITERFASSUNG_ITEM} t={t} badge={0} />
                : <SidebarItem item={ZEITERFASSUNG_ITEM} t={t} badge={0} />
            )}
            {isVisible(KALENDER_ITEM, permissions, isDev) && (
              !showLabels
                ? <CollapsedIcon item={KALENDER_ITEM} t={t} badge={0} />
                : <SidebarItem item={KALENDER_ITEM} t={t} badge={0} />
            )}

            {/* Main groups — filtered by focus mode + permissions */}
            {SIDEBAR_GROUPS.map(s => {
              const items = s.items.filter(i => isVisible(i, permissions, isDev) && (!i.modes || i.modes.includes(focusMode)));
              return renderGroup(s, items);
            })}
          </>)}
        </nav>

        {/* ── Footer ── */}
        <div className="px-3 py-2 border-t border-[var(--sidebar-border)] flex-shrink-0">
          {showLabels ? (<>
            {/* Admin Hub link — admin roles only */}
            {permissions?.admin && permissions.admin !== 'none' && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  `flex items-center gap-3 w-full px-4 py-2 rounded-[10px] transition-colors ${
                    isActive ? 'text-[var(--sidebar-active)] font-semibold' : 'text-[var(--sidebar-text)] hover:text-[var(--sidebar-text-hover)] hover:bg-[var(--sidebar-bg-hover)]'
                  }`
                }
              >
                <Settings className="w-[18px] h-[18px]" />
                <span className="text-[14px] font-medium">{t('adminHub.title')}</span>
              </NavLink>
            )}
            {/* Bug-Reports link — admin only with badge */}
            {permissions?.admin && permissions.admin !== 'none' && (
              <NavLink
                to="/admin/bug-reports"
                className={({ isActive }) =>
                  `flex items-center gap-3 w-full px-4 py-2 rounded-[10px] transition-colors ${
                    isActive ? 'text-[var(--sidebar-active)] font-semibold' : 'text-[var(--sidebar-text)] hover:text-[var(--sidebar-text-hover)] hover:bg-[var(--sidebar-bg-hover)]'
                  }`
                }
              >
                <Bug className="w-[18px] h-[18px]" />
                <span className="text-[14px] font-medium flex-1">{t('nav.bug_reports') || 'Bug-Reports'}</span>
                {badges.bug_reports > 0 && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-rose-500 text-white">
                    {badges.bug_reports}
                  </span>
                )}
              </NavLink>
            )}
            {/* Help links */}
            <div className="flex items-center gap-3 px-4 py-1.5 text-[11px] text-[var(--sidebar-footer-text)]">
              <NavLink to="/handbuch" className="hover:text-[var(--sidebar-footer-hover)] transition-colors flex items-center gap-1">
                <BookOpen className="w-3 h-3" /> {t('nav.manual')}
              </NavLink>
              <NavLink to="/dev-handbuch" className="hover:text-[var(--sidebar-footer-hover)] transition-colors flex items-center gap-1">
                <Code className="w-3 h-3" /> Dev
              </NavLink>
              <NavLink to="/changelog" className="hover:text-[var(--sidebar-footer-hover)] transition-colors flex items-center gap-1">
                <ClipboardList className="w-3 h-3" /> Log
              </NavLink>
            </div>
            {/* Collapse button */}
            <button
              onClick={() => setSidebarMode(isPinned ? 'collapsed' : 'pinned')}
              className="flex items-center gap-3 w-full px-4 py-2.5 rounded-[10px] text-[var(--sidebar-text)] hover:text-[var(--sidebar-text-hover)] hover:bg-[var(--sidebar-bg-hover)] transition-colors"
            >
              {isPinned ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M11 19l-7-7 7-7M18 19l-7-7 7-7"/></svg>
              ) : (
                <Pin className="w-[18px] h-[18px]" />
              )}
              <span className="text-[14px] font-medium">{isPinned ? t('nav.collapse') : t('sidebar.anpinnen')}</span>
            </button>
          </>) : (<>
            {/* Admin Hub icon — collapsed mode */}
            {permissions?.admin && permissions.admin !== 'none' && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  `flex items-center justify-center w-10 h-10 mx-auto rounded-[10px] transition-colors mb-1 ${
                    isActive ? 'text-[var(--sidebar-active)]' : 'text-[var(--sidebar-category)] hover:text-[var(--sidebar-text-hover)] hover:bg-[var(--sidebar-bg-hover)]'
                  }`
                }
                title={t('adminHub.title')}
              >
                <Settings className="w-[18px] h-[18px]" />
              </NavLink>
            )}
            {/* Bug-Reports icon — collapsed mode (admin only) */}
            {permissions?.admin && permissions.admin !== 'none' && (
              <NavLink
                to="/admin/bug-reports"
                className={({ isActive }) =>
                  `relative flex items-center justify-center w-10 h-10 mx-auto rounded-[10px] transition-colors mb-1 ${
                    isActive ? 'text-[var(--sidebar-active)]' : 'text-[var(--sidebar-category)] hover:text-[var(--sidebar-text-hover)] hover:bg-[var(--sidebar-bg-hover)]'
                  }`
                }
                title={t('nav.bug_reports') || 'Bug-Reports'}
              >
                <Bug className="w-[18px] h-[18px]" />
                {badges.bug_reports > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 text-[9px] font-semibold min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full bg-rose-500 text-white">
                    {badges.bug_reports}
                  </span>
                )}
              </NavLink>
            )}
            {/* Expand button */}
            <button
              onClick={() => setSidebarMode('pinned')}
              className="flex items-center justify-center w-10 h-10 mx-auto rounded-[10px] text-[var(--sidebar-text)] hover:text-[var(--sidebar-text-hover)] hover:bg-[var(--sidebar-bg-hover)] transition-colors"
              title={t('nav.expand_sidebar')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M13 5l7 7-7 7M6 5l7 7-7 7"/></svg>
            </button>
          </>)}
        </div>
      </div>

      {/* ── Flyout (collapsed mode) ── */}
      {!showLabels && flyoutItem && (
        <Flyout item={flyoutItem} t={t} badges={badges} permissions={permissions} onMouseEnter={keepFlyout} onMouseLeave={hideFlyout} />
      )}
    </aside>
  );
}
